import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Map as MapIcon, Loader2 } from 'lucide-react';
import { RouteConfirmationMap } from './RouteConfirmationMap';
import { RouteWithDirections } from './RouteWithDirections';
import { RouteSummaryCard } from './RouteSummaryCard';
import { StartLocationSelector } from './StartLocationSelector';
import { CustomAddressDialog } from './CustomAddressDialog';
import { DealerLocation, TechnicianRoute, RouteStop } from '../types';
import { optimizeRouteStops } from '../utils/routeOptimization';
import { dealerInfoId } from '../utils/routeBoardMapping';
import { optimizeRouteWithMapbox } from '../utils/mapboxOptimization';
import { reverseGeocodeForPostcode } from '../utils/mapboxGeocoding';
import {
  RouteStop as DatabaseRouteStop,
  RouteStop as StoredRouteStop,
  TechnicianRouteWithDetails
} from '@/types/technicianRoutes';
import { TechnicianAssignment, ActiveTechnician } from '@/lib/supabase/technicianAssignments';
import { cn } from '@/lib/utils';
import { useRecallViewTracking } from '@/features/routing/hooks/useRecallViewTracking';
import { isRecallProcessed } from '@/lib/supabase/assignmentStatus';

/**
 * Preview stop id: household for customer stops (all named 'Customer stop'), dealer name
 * otherwise. Matches dealerInfoId for customers so preview, board and print agree.
 */
function previewStopId(s: {
  stop_kind?: string | null | undefined;
  contact_id?: string | null | undefined;
  dealer_name?: string | null | undefined;
  dealer_address?: string | null | undefined;
  address_line2?: string | null | undefined;
}): string {
  if (s.stop_kind === 'customer') {
    return dealerInfoId({
      dealer_id: '',
      dealer_name: s.dealer_name || '',
      stop_kind: 'customer',
      contact_id: s.contact_id ?? null,
      dealer_address: s.dealer_address || '',
      address_line2: s.address_line2 ?? null
    });
  }
  return s.dealer_name || 'Unknown';
}

const storedStopPreviewId = (s: StoredRouteStop) => previewStopId(s);

type PreviewRecallEntry = {
  vin: string;
  campaign_number: string;
  recall_title: string;
  car_year?: string | number;
  make_name?: string;
  model_name?: string;
  stock_number?: string;
  dealer_address?: string;
  dealer_city?: string;
  dealer_region?: string;
  dealer_postal_code?: string;
  stop_type?: 'recall' | 'manual';
  manual_stop_reason?: string;
  stop_kind?: 'business' | 'customer';
  contact_id?: string | null;
  dispatcher_note?: string | null;
};

/** Fields a recall/manual-stop row may carry into the preview (customer fields optional). */
type PreviewSourceRecall = {
  vin: string;
  campaign_number: string;
  recall_title?: string | undefined;
  year?: string | number | undefined;
  make?: string | undefined;
  model?: string | undefined;
  stock_number?: string | undefined;
  dealer_address?: string | undefined;
  dealer_city?: string | undefined;
  dealer_region?: string | undefined;
  dealer_postal_code?: string | undefined;
  stop_type?: 'recall' | 'manual' | undefined;
  manual_stop_reason?: string | undefined;
  stop_kind?: 'business' | 'customer' | undefined;
  contact_id?: string | null | undefined;
  address_line2?: string | null | undefined;
  dispatcher_note?: string | null | undefined;
};

// eslint-disable-next-line complexity -- flat optional-field copy
function toPreviewRecallEntry(recall: PreviewSourceRecall): PreviewRecallEntry {
  const entry: PreviewRecallEntry = {
    vin: recall.vin,
    campaign_number: recall.campaign_number,
    recall_title: recall.recall_title || ''
  };
  if (recall.year !== undefined) entry.car_year = recall.year;
  if (recall.make !== undefined) entry.make_name = recall.make;
  if (recall.model !== undefined) entry.model_name = recall.model;
  if (recall.stock_number !== undefined) entry.stock_number = recall.stock_number;
  if (recall.dealer_address !== undefined) entry.dealer_address = recall.dealer_address;
  if (recall.dealer_city !== undefined) entry.dealer_city = recall.dealer_city;
  if (recall.dealer_region !== undefined) entry.dealer_region = recall.dealer_region;
  if (recall.dealer_postal_code !== undefined) entry.dealer_postal_code = recall.dealer_postal_code;
  if (recall.stop_type) entry.stop_type = recall.stop_type;
  if (recall.manual_stop_reason) entry.manual_stop_reason = recall.manual_stop_reason;
  if (recall.stop_kind) entry.stop_kind = recall.stop_kind;
  if (recall.contact_id) entry.contact_id = recall.contact_id;
  if (recall.dispatcher_note) entry.dispatcher_note = recall.dispatcher_note;
  return entry;
}

interface RoutePreviewPanelProps {
  selectedTechnician: ActiveTechnician | null;
  assignments: TechnicianAssignment[];
  recalls: Array<{
    vin: string;
    campaign_number: string;
    dealer_name?: string;
    dealer_address?: string;
    dealer_city?: string;
    dealer_region?: string;
    dealer_postal_code?: string;
    dealer_phone?: string;
    dealer_latitude?: number;
    dealer_longitude?: number;
    recall_title?: string;
    year?: string | number;
    make?: string;
    model?: string;
    stock_number?: string;
  }>;
  baseLocation?: { lat: number; lng: number } | null;
  baseLocationName?: string;
  selectedRecallKeys?: Set<string>;
  selectedDate: Date; // The date for which to show the route
  className?: string;
  // Lifted state for custom start location (persists across recall changes)
  locationMode: 'default' | 'custom';
  onLocationModeChange: (mode: 'default' | 'custom') => void;
  customLocation: { lat: number; lng: number; displayName: string } | null;
  onCustomLocationChange: (
    location: { lat: number; lng: number; displayName: string } | null
  ) => void;
  // Callback to expose route data to parent
  onRouteChange?: (
    route: TechnicianRoute | null,
    effectiveBaseLocation: { lat: number; lng: number } | null,
    effectiveLocationName: string | undefined
  ) => void;
  // Stored route from database (single source of truth for stop order)
  storedRoute?: TechnicianRouteWithDetails | null;
}

/**
 * Route preview panel with map and summary stats
 * Shows the selected technician's route with numbered stops
 */
// Pre-existing: this component was 789 lines against a 600 limit before the
// lifecycle work touched it; extracting useRecallViewTracking trimmed it slightly.
// Splitting it belongs with the route-preview fan-out fix (RLM-06) and the
// stop-identity work (Phase 5a), both of which restructure this file anyway.
// eslint-disable-next-line max-lines-per-function
export const RoutePreviewPanel: React.FC<RoutePreviewPanelProps> = ({
  selectedTechnician,
  assignments,
  recalls,
  baseLocation,
  baseLocationName,
  selectedRecallKeys = new Set(),
  selectedDate,
  className = '',
  locationMode,
  onLocationModeChange,
  customLocation,
  onCustomLocationChange,
  onRouteChange,
  storedRoute
}) => {
  const [routeDistance, setRouteDistance] = useState(0);
  const [routeDuration, setRouteDuration] = useState(0);
  const useDirections = true;

  // Address dialog state (local, only controls dialog visibility)
  const [showAddressDialog, setShowAddressDialog] = useState(false);

  // Mapbox optimization state (debounced async optimization)
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [mapboxOptimizedStops, setMapboxOptimizedStops] = useState<RouteStop[] | null>(null);
  const mapboxDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Enriched postal codes from reverse geocoding (for manual stops missing postal code)
  const [enrichedPostalCodes, setEnrichedPostalCodes] = useState<Map<string, string>>(new Map());
  // Every postal lookup already attempted this session, successful or not, so a
  // stop that has no postcode is not re-queried against the metered Mapbox API.
  const attemptedPostalLookups = useRef<Set<string>>(new Set());

  // Compute effective base location based on mode selection
  const effectiveBaseLocation = useMemo(() => {
    if (locationMode === 'custom' && customLocation) {
      return { lat: customLocation.lat, lng: customLocation.lng };
    }
    return baseLocation;
  }, [locationMode, customLocation, baseLocation]);

  // Compute effective location name for display (address, city, state, zip)
  const effectiveLocationName = useMemo(() => {
    if (locationMode === 'custom' && customLocation) {
      // Parse Nominatim response format: "123 Main St, Detroit, Wayne County, Michigan, 48201, United States"
      // Extract: address, city, state, zip
      const parts = customLocation.displayName.split(',').map(p => p.trim());
      if (parts.length >= 4) {
        // Find zip code (5 digits) and state
        const zipIndex = parts.findIndex(p => /^\d{5}(-\d{4})?$/.test(p));
        const stateIndex = zipIndex > 0 ? zipIndex - 1 : -1;

        // Get street address (first part)
        const street = parts[0];
        // Get city (second part, skip if it's a number like house number)
        const city = parts[1];
        // Get state abbreviation or full name
        const state = stateIndex > 0 ? parts[stateIndex] : '';
        // Get zip
        const zip = zipIndex > 0 ? parts[zipIndex] : '';

        if (street && city && state && zip) {
          return `${street}, ${city}, ${state} ${zip}`;
        }
      }
      // Fallback: return first 4 meaningful parts
      return parts.slice(0, 4).join(', ');
    }
    return baseLocationName;
  }, [locationMode, customLocation, baseLocationName]);

  // Handle location mode change
  const handleLocationModeChange = useCallback(
    (mode: 'default' | 'custom') => {
      if (mode === 'custom' && !customLocation) {
        // Open dialog when switching to custom without an existing address
        setShowAddressDialog(true);
      }
      onLocationModeChange(mode);
    },
    [customLocation, onLocationModeChange]
  );

  // Handle custom location confirmation from dialog
  const handleCustomLocationConfirm = useCallback(
    (location: { lat: number; lng: number; displayName: string }) => {
      onCustomLocationChange(location);
      onLocationModeChange('custom');
      setShowAddressDialog(false);
      // Reset route calculations to trigger recalculation
      setRouteDistance(0);
      setRouteDuration(0);
    },
    [onCustomLocationChange, onLocationModeChange]
  );

  // Reset route calculations when technician or date changes
  useEffect(() => {
    setRouteDistance(0);
    setRouteDuration(0);
  }, [selectedTechnician?.id, selectedDate]);

  // Helper to check if an assignment's expected_visit_date matches the selected date
  // IMPORTANT: Extract the date part and compare as strings to avoid UTC timezone shift
  // Database returns TIMESTAMPTZ like "2026-01-27T00:00:00+00:00" but we only care about the date
  const isDateMatch = useCallback(
    (expectedVisitDate: string | undefined): boolean => {
      if (!expectedVisitDate) return false;

      // Extract YYYY-MM-DD from the expected_visit_date string
      // Handles both date-only ("2026-01-27") and timestamps ("2026-01-27T00:00:00+00:00")
      const dateMatch =
        typeof expectedVisitDate === 'string' && expectedVisitDate.match(/^(\d{4}-\d{2}-\d{2})/);

      if (!dateMatch || !dateMatch[1]) return false;

      // Format selectedDate as YYYY-MM-DD for comparison
      const selectedYear = selectedDate.getFullYear();
      const selectedMonth = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const selectedDay = String(selectedDate.getDate()).padStart(2, '0');
      const selectedDateStr = `${selectedYear}-${selectedMonth}-${selectedDay}`;

      return dateMatch[1] === selectedDateStr;
    },
    [selectedDate]
  );

  // Stable YYYY-MM-DD for the day on screen, so tracking keys on a value rather
  // than on a Date object that is a new instance every render.
  const selectedDateKey = useMemo(() => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, [selectedDate]);

  // What this route actually shows: one technician, one day, work that is still
  // live. Hoisted so the route below and the lifecycle tracking agree on what
  // "visible" means — they used to decide it separately, and tracking decided it
  // over every assignment in the tenant.
  //
  // isRecallProcessed replaces an inline `!== completed && !== cancelled` pair,
  // one of the copies Phase 3b is consolidating. It also covers the legacy
  // `not_there`, which the inline version missed.
  const visibleAssignments = useMemo(() => {
    if (!selectedTechnician) return [];
    return assignments.filter(
      a =>
        a.technician_id === selectedTechnician.id &&
        !isRecallProcessed(a.assignment_status) &&
        isDateMatch(a.expected_visit_date)
    );
  }, [assignments, selectedTechnician, isDateMatch]);

  useRecallViewTracking({
    technicianId: selectedTechnician?.id,
    dateKey: selectedDateKey,
    visibleAssignments,
    allAssignments: assignments
  });

  // Build route data from assignments AND selected recalls for the selected technician
  // Treats selected recalls as if they're already assigned

  const { route, dealerLocations, stopCount, totalDistance, totalDuration } = useMemo(() => {
    if (!selectedTechnician) {
      return {
        route: null,
        dealerLocations: [],
        stopCount: 0,
        totalDistance: 0,
        totalDuration: 0
      };
    }

    // Shared with the lifecycle tracking above, so both agree on what is visible.
    const technicianAssignments = visibleAssignments;

    // Get selected recalls that aren't yet assigned to this technician
    const selectedRecallsForRoute = recalls.filter(recall => {
      const key = `${recall.vin}|${recall.campaign_number}`;
      if (!selectedRecallKeys.has(key)) {
        return false; // Not selected
      }

      // Include if not assigned yet (new selection) or if assigned (reassignment)
      return recall.dealer_latitude && recall.dealer_longitude;
    });

    // Combine assigned recalls with selected recalls (treat selected as assigned)
    const allRecallsForRoute = new Map<
      string,
      {
        recall: (typeof recalls)[0];
        isAssigned: boolean;
      }
    >();

    // Add assigned recalls
    technicianAssignments.forEach(assignment => {
      // Skip manual stops - handled separately below
      if (assignment.stop_type === 'manual') return;

      const recallData = recalls.find(
        r => r.vin === assignment.vin && r.campaign_number === assignment.campaign_number
      );
      if (recallData) {
        const key = `${recallData.vin}|${recallData.campaign_number}`;
        allRecallsForRoute.set(key, { recall: recallData, isAssigned: true });
      }
    });

    // Build a map of postal codes from stored route (single source of truth has enriched data)
    // Keyed by preview stop id (household for customer stops, dealer name otherwise) — every
    // customer stop is named 'Customer stop', so a name key would cross customers' ZIPs.
    const storedPostalCodes = new Map<string, string>();
    (storedRoute?.route_data?.stops ?? []).forEach(stop => {
      if (stop.dealer_postal_code) {
        storedPostalCodes.set(storedStopPreviewId(stop), stop.dealer_postal_code);
      }
    });

    // Add manual stops (they don't have matching recalls in the recalls array)
    technicianAssignments.forEach(assignment => {
      if (assignment.stop_type === 'manual') {
        const key = `manual-${assignment.id}`;
        // Get postal code from assignment, stored route, or enriched (reverse geocoded)
        const enrichedKey = `${assignment.dealer_name}|${assignment.manual_stop_lat}|${assignment.manual_stop_lng}`;
        const postalCode =
          assignment.dealer_postal_code ||
          storedPostalCodes.get(
            previewStopId({
              stop_kind: assignment.stop_kind,
              contact_id: assignment.contact_id,
              dealer_name: assignment.dealer_name,
              dealer_address: assignment.dealer_address,
              address_line2: assignment.address_line2
            })
          ) ||
          enrichedPostalCodes.get(enrichedKey) ||
          '';

        // Create a synthetic recall entry from manual stop assignment data
        allRecallsForRoute.set(key, {
          recall: {
            vin: assignment.vin || '',
            campaign_number: assignment.campaign_number || '',
            recall_title:
              assignment.recall_title ||
              `${assignment.manual_stop_reason || 'Manual Stop'} @ ${assignment.dealer_name}`,
            dealer_name: assignment.dealer_name,
            dealer_address: assignment.dealer_address,
            dealer_city: assignment.dealer_city,
            dealer_region: assignment.dealer_region,
            dealer_postal_code: postalCode,
            dealer_phone: assignment.dealer_phone || '',
            dealer_latitude: assignment.manual_stop_lat,
            dealer_longitude: assignment.manual_stop_lng,
            year: assignment.vehicle_year,
            make: assignment.vehicle_make,
            model: assignment.vehicle_model,
            stop_type: 'manual' as const,
            manual_stop_reason: assignment.manual_stop_reason,
            ...(assignment.stop_kind && { stop_kind: assignment.stop_kind }),
            ...(assignment.contact_id && { contact_id: assignment.contact_id }),
            ...(assignment.address_line2 && { address_line2: assignment.address_line2 }),
            ...(assignment.dispatcher_note && { dispatcher_note: assignment.dispatcher_note })
          } as (typeof recalls)[0] & { stop_type: 'manual'; manual_stop_reason?: string },
          isAssigned: true
        });
      }
    });

    // Add selected recalls (overwrites if already in map, which is fine)
    selectedRecallsForRoute.forEach(recall => {
      const key = `${recall.vin}|${recall.campaign_number}`;
      allRecallsForRoute.set(key, { recall, isAssigned: false });
    });

    if (allRecallsForRoute.size === 0) {
      return {
        route: null,
        dealerLocations: [],
        stopCount: 0,
        totalDistance: 0,
        totalDuration: 0
      };
    }

    // Group all recalls by dealer for route stops
    const dealerMap = new Map<
      string,
      {
        dealer: DealerLocation;
        recalls: PreviewRecallEntry[];
        stopKind?: 'business' | 'customer';
        contactId?: string | null;
        addressLine2?: string | null;
      }
    >();

    allRecallsForRoute.forEach(({ recall: rawRecall }) => {
      const recall = rawRecall as typeof rawRecall & PreviewSourceRecall;
      if (!recall.dealer_latitude || !recall.dealer_longitude) {
        return; // Skip if no coordinates
      }

      const dealerKey = previewStopId(recall);
      const isCustomer = recall.stop_kind === 'customer';

      if (!dealerMap.has(dealerKey)) {
        dealerMap.set(dealerKey, {
          ...(isCustomer && {
            stopKind: 'customer' as const,
            contactId: recall.contact_id ?? null,
            addressLine2: recall.address_line2 ?? null
          }),
          dealer: {
            id: dealerKey,
            name: recall.dealer_name || 'Unknown',
            address: recall.dealer_address || '',
            city: recall.dealer_city || '',
            region: recall.dealer_region || '',
            postal_code: recall.dealer_postal_code || '',
            phone: recall.dealer_phone || '',
            latitude: recall.dealer_latitude,
            longitude: recall.dealer_longitude,
            recall_count: 0,
            skill_level: selectedTechnician.skill_level
          },
          recalls: []
        });
      }

      const entry = dealerMap.get(dealerKey);
      if (!entry) return;
      entry.recalls.push(toPreviewRecallEntry(recall));
      entry.dealer.recall_count = entry.recalls.length;
    });

    // Convert to arrays
    const dealers = Array.from(dealerMap.values());
    const dealerLocationsList: DealerLocation[] = dealers.map(d => d.dealer);

    // Build route stops
    let stops: RouteStop[] = dealers.map((d, index) => {
      // Map marker / route color: use manual (accent) styling only when this dealer has
      // manual stops exclusively. If Vehix + manual are mixed at one dealer, use default Vehix.
      const hasManual = d.recalls.some(r => r.stop_type === 'manual');
      const hasVehix = d.recalls.some(r => r.stop_type !== 'manual');
      const isManualOnly = hasManual && !hasVehix;

      return {
        id: `stop-${index}`,
        dealer: d.dealer,
        arrival_time: '',
        departure_time: '',
        service_duration: 30, // Default 30 min per stop
        stop_order: index + 1,
        recalls: d.recalls,
        ...(isManualOnly && { stop_type: 'manual' as const }),
        ...(d.stopKind && { stop_kind: d.stopKind }),
        ...(d.contactId && { contact_id: d.contactId }),
        ...(d.addressLine2 && { address_line2: d.addressLine2 })
      };
    });

    // Determine if the current stop set matches the stored route exactly
    const storedStops = storedRoute?.route_data?.stops ?? [];
    const currentDealerNames = new Set(stops.map(s => s.dealer.id));
    const storedDealerNames = new Set(storedStops.map(storedStopPreviewId));
    const routeMatchesStored =
      storedStops.length > 0 &&
      currentDealerNames.size === storedDealerNames.size &&
      [...currentDealerNames].every(name => storedDealerNames.has(name));

    if (routeMatchesStored) {
      // Route is unchanged from database — use stored order (single source of truth)
      const storedStopOrder = new Map(storedStops.map((s, idx) => [storedStopPreviewId(s), idx]));
      stops.sort((a, b) => {
        const orderA = storedStopOrder.get(a.dealer.id) ?? Infinity;
        const orderB = storedStopOrder.get(b.dealer.id) ?? Infinity;
        return orderA - orderB;
      });
      stops.forEach((stop, idx) => {
        stop.stop_order = idx + 1;
      });
    } else if (mapboxOptimizedStops && mapboxOptimizedStops.length === stops.length) {
      // Use Mapbox-optimized stops (async result from debounced API call)
      // Match by stop id (unique per household / dealer) since order may differ
      const optimizedOrderMap = new Map(mapboxOptimizedStops.map(s => [s.dealer.id, s.stop_order]));
      stops.sort((a, b) => {
        const orderA = optimizedOrderMap.get(a.dealer.id) ?? Infinity;
        const orderB = optimizedOrderMap.get(b.dealer.id) ?? Infinity;
        return orderA - orderB;
      });
      stops.forEach((stop, idx) => {
        stop.stop_order = idx + 1;
      });
    } else if (effectiveBaseLocation && stops.length > 1) {
      // Route has been modified — use nearest-neighbor as immediate fallback
      // Mapbox optimization will run async and update when ready
      stops = optimizeRouteStops(stops, effectiveBaseLocation);
    }

    // Use actual route calculations if available, otherwise estimate
    const estimatedDistance =
      routeDistance > 0 ? routeDistance : Math.max(0, (stops.length - 1) * 15);
    const estimatedDuration =
      routeDuration > 0 ? routeDuration : stops.length * 30 + Math.max(0, (stops.length - 1) * 20);

    const previewHomeBase =
      effectiveBaseLocation &&
      !isNaN(effectiveBaseLocation.lat) &&
      !isNaN(effectiveBaseLocation.lng)
        ? { latitude: effectiveBaseLocation.lat, longitude: effectiveBaseLocation.lng }
        : undefined;

    // Build the technician route object
    const technicianRoute: TechnicianRoute = {
      technician_id: selectedTechnician.id,
      technician: {
        id: selectedTechnician.id,
        full_name: selectedTechnician.full_name,
        skill_level: selectedTechnician.skill_level,
        phone_number: selectedTechnician.phone_number || '',
        email: selectedTechnician.email || '',
        ...(previewHomeBase ? { home_base: previewHomeBase } : {})
      },
      stops,
      total_distance: estimatedDistance,
      total_duration: estimatedDuration,
      route_color: '#d4854a' // Brand primary color
    };

    return {
      route: technicianRoute,
      dealerLocations: dealerLocationsList,
      stopCount: stops.length,
      totalDistance: estimatedDistance,
      totalDuration: estimatedDuration
    };
  }, [
    selectedTechnician,
    visibleAssignments,
    recalls,
    effectiveBaseLocation,
    selectedRecallKeys,
    routeDistance,
    routeDuration,
    storedRoute,
    mapboxOptimizedStops,
    enrichedPostalCodes
  ]);

  // Enrich manual stops with missing postal codes via reverse geocoding.
  //
  // RLM-64: this filtered on `a.status`, which TechnicianAssignment does not
  // have, so from 7b614df5 (2026-04-23) until 2026-09-11 it matched nothing and
  // never ran. Correcting the field switches on metered Mapbox calls, so each
  // stop is now looked up at most ONCE per session — including lookups that
  // fail or find no postcode. Only successes used to be remembered, so an
  // unresolvable stop would have been re-queried on every assignment refetch,
  // every technician switch, and every time another stop resolved.
  useEffect(() => {
    const technicianAssignments = assignments.filter(
      a => a.technician_id === selectedTechnician?.id && a.assignment_status === 'assigned'
    );

    // Helper to get coords from assignment (check multiple field names)
    const getCoords = (a: (typeof technicianAssignments)[0]) => {
      const lat = a.manual_stop_lat;
      const lng = a.manual_stop_lng;
      return { lat, lng };
    };
    const postalKey = (a: (typeof technicianAssignments)[0]) => {
      const { lat, lng } = getCoords(a);
      return `${a.dealer_name}|${lat}|${lng}`;
    };

    // Manual stops missing a postal code that we have not already tried
    const manualStopsMissingPostal = technicianAssignments.filter(a => {
      if (a.stop_type !== 'manual') return false;
      if (a.dealer_postal_code) return false; // Already has postal code
      const { lat, lng } = getCoords(a);
      if (!lat || !lng) return false; // No coords

      const key = postalKey(a);
      return !enrichedPostalCodes.has(key) && !attemptedPostalLookups.current.has(key);
    });

    if (manualStopsMissingPostal.length === 0) return;

    // Mark them attempted BEFORE awaiting, so an overlapping run of this effect
    // cannot start the same lookup a second time.
    manualStopsMissingPostal.forEach(a => attemptedPostalLookups.current.add(postalKey(a)));

    // Fetch postal codes for all missing ones
    const fetchPostalCodes = async () => {
      const newPostalCodes = new Map<string, string>();

      for (const assignment of manualStopsMissingPostal) {
        const { lat, lng } = getCoords(assignment);

        try {
          const result = await reverseGeocodeForPostcode(lat!, lng!);

          if (result?.postcode) {
            newPostalCodes.set(postalKey(assignment), result.postcode);
          }
        } catch {
          // Reverse geocode failed - continue without postal code
        }
      }

      if (newPostalCodes.size > 0) {
        setEnrichedPostalCodes(prev => {
          const merged = new Map(prev);
          newPostalCodes.forEach((value, key) => merged.set(key, value));
          return merged;
        });
      }
    };

    fetchPostalCodes();
  }, [assignments, selectedTechnician?.id, enrichedPostalCodes]);

  // Debounced Mapbox optimization effect
  // Triggers when stops change and route doesn't match stored route
  useEffect(() => {
    // Clear any pending debounce
    if (mapboxDebounceRef.current) {
      clearTimeout(mapboxDebounceRef.current);
      mapboxDebounceRef.current = null;
    }

    // Skip if no route or base location
    if (!route || !effectiveBaseLocation || route.stops.length <= 1) {
      setMapboxOptimizedStops(null);
      setIsOptimizing(false);
      return;
    }

    // Check if route matches stored (don't optimize if using stored order)
    const storedStops = storedRoute?.route_data?.stops ?? [];
    const currentDealerNames = new Set(route.stops.map(s => s.dealer.name));
    const storedDealerNames = new Set(storedStops.map(s => s.dealer_name));
    const routeMatchesStored =
      storedStops.length > 0 &&
      currentDealerNames.size === storedDealerNames.size &&
      [...currentDealerNames].every(name => storedDealerNames.has(name));

    if (routeMatchesStored) {
      setMapboxOptimizedStops(null);
      setIsOptimizing(false);
      return;
    }

    // Debounce Mapbox API call (500ms)
    setIsOptimizing(true);
    mapboxDebounceRef.current = setTimeout(async () => {
      try {
        // Convert RouteStop to DatabaseRouteStop format for Mapbox function
        const stopsForMapbox: DatabaseRouteStop[] = route.stops.map(stop => ({
          stop_order: stop.stop_order,
          dealer_id: stop.dealer.id,
          dealer_name: stop.dealer.name,
          dealer_address: stop.dealer.address,
          dealer_city: stop.dealer.city,
          dealer_region: stop.dealer.region,
          lat: stop.dealer.latitude,
          lng: stop.dealer.longitude,
          recalls: (stop.recalls ?? []).map(r => ({
            assignment_id: '',
            vin: r.vin,
            campaign_number: r.campaign_number,
            recall_title: r.recall_title,
            vehicle_year: typeof r.car_year === 'number' ? r.car_year : null,
            vehicle_make: r.make_name || '',
            vehicle_model: r.model_name || '',
            estimated_duration_minutes: 30,
            status: 'assigned' as const
          })),
          recall_count: stop.recalls?.length ?? 0,
          distance_from_previous_miles: 0,
          duration_from_previous_minutes: 0
        }));

        const result = await optimizeRouteWithMapbox(stopsForMapbox, effectiveBaseLocation);

        // Convert result back to RouteStop format
        const optimizedStops: RouteStop[] = result.stops.map((dbStop, idx) => {
          // Find the original stop (dealer_id carries the preview stop id) to keep full data
          const originalStop = route.stops.find(s => s.dealer.id === dbStop.dealer_id);
          if (originalStop) {
            return {
              ...originalStop,
              stop_order: idx + 1
            };
          }
          // Fallback (shouldn't happen)
          return {
            id: `stop-${idx}`,
            dealer: {
              id: dbStop.dealer_id,
              name: dbStop.dealer_name,
              address: dbStop.dealer_address,
              city: dbStop.dealer_city,
              region: dbStop.dealer_region,
              postal_code: '',
              phone: '',
              latitude: dbStop.lat,
              longitude: dbStop.lng,
              recall_count: dbStop.recall_count,
              skill_level: 2
            },
            recalls: [],
            arrival_time: '',
            departure_time: '',
            service_duration: 30,
            stop_order: idx + 1
          };
        });

        setMapboxOptimizedStops(optimizedStops);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[RoutePreviewPanel] Mapbox optimization failed:', error);
        // Keep using nearest-neighbor fallback
      } finally {
        setIsOptimizing(false);
      }
    }, 500);

    return () => {
      if (mapboxDebounceRef.current) {
        clearTimeout(mapboxDebounceRef.current);
      }
    };
  }, [route?.stops.length, effectiveBaseLocation, storedRoute]);

  // Notify parent of route changes
  useEffect(() => {
    if (onRouteChange) {
      onRouteChange(route, effectiveBaseLocation ?? null, effectiveLocationName);
    }
  }, [route, effectiveBaseLocation, effectiveLocationName, onRouteChange]);

  const routes = route ? [route] : [];

  // Handle route update from routing machine
  const handleRouteUpdate = useCallback(
    (_route: TechnicianRoute, distance: number, duration: number) => {
      setRouteDistance(distance);
      setRouteDuration(duration);
    },
    []
  );

  return (
    <div
      className={cn(
        'flex flex-col bg-card rounded-lg border overflow-hidden transition-all duration-300',
        className
      )}
    >
      {/* Header */}
      <div className='flex items-center justify-between px-4 py-2 border-b bg-muted/30 gap-2'>
        {/* Left: Title */}
        <div className='flex items-center gap-2 flex-shrink-0'>
          <MapIcon className='h-4 w-4 text-muted-foreground' />
          <span className='font-medium text-sm text-foreground whitespace-nowrap'>
            Route Preview
          </span>
          {stopCount > 0 && (
            <span className='text-xs text-muted-foreground whitespace-nowrap'>
              ({stopCount} stop{stopCount !== 1 ? 's' : ''})
            </span>
          )}
          {isOptimizing && (
            <span className='flex items-center gap-1 text-xs text-muted-foreground'>
              <Loader2 className='h-3 w-3 animate-spin' />
              Optimizing...
            </span>
          )}
        </div>

        {/* Right: Start Location Selector */}
        <div className='flex-1 min-w-0'>
          <StartLocationSelector
            mode={locationMode}
            onModeChange={handleLocationModeChange}
            defaultLocationName={baseLocationName ?? ''}
            customLocationName={effectiveLocationName ?? ''}
            onEditCustom={() => setShowAddressDialog(true)}
          />
        </div>
      </div>

      {/* Content */}
      <div className='flex-1 flex flex-col min-h-0'>
        {/* Map */}
        <div
          className='flex-1 min-h-[300px] bg-background dark:bg-background route-preview-map-container'
          style={{ position: 'relative' }}
        >
          {selectedTechnician ? (
            (() => {
              const shouldUseDirections =
                useDirections &&
                effectiveBaseLocation &&
                !isNaN(effectiveBaseLocation.lat) &&
                !isNaN(effectiveBaseLocation.lng) &&
                stopCount > 0;

              const routeKey = [
                'route-directions',
                selectedTechnician?.id || 'preview',
                routes.length,
                effectiveBaseLocation?.lat?.toFixed(4) || 'none',
                effectiveBaseLocation?.lng?.toFixed(4) || 'none'
              ].join('-');

              return shouldUseDirections ? (
                <RouteWithDirections
                  key={routeKey}
                  dealerLocations={dealerLocations}
                  routes={routes}
                  showRouteLines={true}
                  selectedRouteId={selectedTechnician?.id}
                  baseLocation={effectiveBaseLocation}
                  onRouteUpdate={handleRouteUpdate}
                />
              ) : (
                <RouteConfirmationMap
                  dealerLocations={dealerLocations}
                  routes={routes}
                  showRouteLines={true}
                  selectedRouteId={selectedTechnician?.id}
                  baseLocation={effectiveBaseLocation ?? null}
                />
              );
            })()
          ) : (
            <div className='h-full flex items-center justify-center bg-muted/20'>
              <div className='text-center'>
                <MapIcon className='h-12 w-12 text-muted-foreground/50 mx-auto mb-3' />
                <p className='text-sm text-muted-foreground'>
                  Select a technician to see route preview
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className='p-4 border-t'>
          <RouteSummaryCard
            stopCount={stopCount}
            totalDistance={routeDistance > 0 ? routeDistance : totalDistance}
            totalDuration={routeDuration > 0 ? routeDuration : totalDuration}
            technicianName={selectedTechnician?.full_name ?? ''}
          />
        </div>
      </div>

      {/* Custom Address Dialog */}
      <CustomAddressDialog
        open={showAddressDialog}
        onOpenChange={setShowAddressDialog}
        onConfirm={handleCustomLocationConfirm}
        currentAddress={customLocation?.displayName ?? ''}
      />
    </div>
  );
};

export default RoutePreviewPanel;
